import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { IPlayerCollections } from "../../scripts/dataProcessors";
import { TeamID } from "../../scripts/models/common";
import { Modification, PlayerModel, RosterLocation, TeamModel } from "../../scripts/models/models";
import { getSnapshotData, Snapshot } from "../../scripts/dataSources/snapshots";
import { labelText, Rating } from "../../scripts/models/ratings";
import { Settings } from "../../scripts/settings";
import { StarMath } from "../../scripts/starMath";
import { average, max, min, standardRound } from "../../scripts/utility";
import TeamIcon from "../common/teamIcon/TeamIcon";
import Tooltip, { TooltipVisibility } from "../common/tooltip/Tooltip";
import { DataContext } from "../context/dataLoader/DataLoader";
import { SettingsContext } from "../context/settings/SettingsLoader";

const plotSize = 500 // 500px square for plots

interface props {
    xAxisCategory: Rating,
    yAxisCategory: Rating,
    teamSet?: TeamModel[],
    titleText: string,
    emphasizedTeam?: TeamID
    snapshot?: Snapshot,
    excludedMods?: string[]
}


function getStarsFromTeam(rating: Rating, team: TeamModel, playerCollections: IPlayerCollections, excludedMods: string[]) {
    let positions: RosterLocation[] = rating.positionsOverride || [];
    if (!rating.positionsOverride) {
        switch (rating.category) {
            case "batting": positions = ["LINEUP"]; break;
            case "running": positions = ["LINEUP"]; break;
            case "defense": positions = ["LINEUP"]; break;
            case "pitching": positions = ["ROTATION"]; break;
            case "vibes": positions = ["LINEUP", "ROTATION"]; break;
        }
    }

    let players: PlayerModel[] = [];
    positions.forEach(position => {
        players = players.concat(playerCollections.playersByTeam[team.id][position]);
    });

    players = players.filter(player => !player.modifications.some(item => {
        let modification = (item as { modification: Modification }).modification ?? (item as Modification)
        return excludedMods.includes(modification.name)
    }))

    const stars: number[] = [];
    players.forEach(player => {
        stars.push(StarMath.getValueForPlayer(rating, player))
    })

    return average(stars);
}

export const ScatterPlot: FC<props> = (props) => {

    const { xAxisCategory, yAxisCategory, teamSet, titleText, emphasizedTeam, snapshot, excludedMods = [] } = props;


    const [startAtZero, setStartAtZero] = useState(false);
    const { playerCollections, snapshots } = useContext(DataContext)
    const [changingScale, setChangingScale] = useState<boolean>(false)

    useEffect(() => {
        setChangingScale(true);
        const turnOff = () => {
            setChangingScale(false);
        }
        const handle = window.setTimeout(turnOff, 1000);
        return () => {
            window.clearTimeout(handle)
        }
    }, [startAtZero])

    const toggleScale = useCallback(() => {
        setStartAtZero(!startAtZero);
    }, [startAtZero, setStartAtZero]);


    const settings = useContext(SettingsContext);
    const showAverages = Settings.lookupSetting("Show Scatterplot Averages", settings);


    if (!teamSet || !playerCollections) {
        return <div>Loading...</div>
    }


    let teamSetToUse = teamSet;
    let playerCollectionsToUse = playerCollections;


    const snapshotData = getSnapshotData(snapshot, snapshots);
    if (snapshotData) {

        const teamIDs = teamSet.map(team => team.id);
        teamSetToUse = snapshotData.teamCollections.allTeams.filter(team => teamIDs.includes(team.id));

        teamSetToUse.sort((a, b) => teamIDs.indexOf(a.id) - teamIDs.indexOf(b.id));

        playerCollectionsToUse = snapshotData.playerCollections;
    }



    const currentDataXVals = teamSet.map(team => getStarsFromTeam(xAxisCategory, team, playerCollections, excludedMods));
    const currentDataYVals = teamSet.map(team => getStarsFromTeam(yAxisCategory, team, playerCollections, excludedMods));

    const xRange = [currentDataXVals.reduce(min), currentDataXVals.reduce(max)];
    const yRange = [currentDataYVals.reduce(min), currentDataYVals.reduce(max)];


    var xMin = 0;
    var yMin = 0;
    var pxPerStar = 500;
    var scaleRange = 1;


    // Round to half stars, account for startAtZero
    const buffer = 0.15;
    const xMax = Math.ceil((xRange[1] + buffer) * 2) / 2;
    const yMax = Math.ceil((yRange[1] + buffer) * 2) / 2;
    xMin = startAtZero ? 0 : Math.floor((xRange[0] - buffer) * 2) / 2;
    yMin = startAtZero ? 0 : Math.floor((yRange[0] - buffer) * 2) / 2;

    scaleRange = Math.max((xMax - xMin), (yMax - yMin));

    pxPerStar = plotSize / (scaleRange);

    if (isNaN(scaleRange)) {
        scaleRange = 1;
    }


    // Calculate averages
    const excludedTeamIDs: string[] = [""];
    const filteredTeams = teamSetToUse.filter(team => !excludedTeamIDs.includes(team.id))
    const xVals = filteredTeams.map(team => getStarsFromTeam(xAxisCategory, team, playerCollectionsToUse, excludedMods));
    const yVals = filteredTeams.map(team => getStarsFromTeam(yAxisCategory, team, playerCollectionsToUse, excludedMods));
    const xAverage = average(xVals);
    const yAverage = average(yVals);


    const xLabel = labelText(xAxisCategory)
    const yLabel = labelText(yAxisCategory)

    return (

        <div className="TeamScatterPlot serif">
            <button className="ScaleButton" aria-pressed={!startAtZero} onClick={toggleScale}>Zoom</button>
            <div className="Header">
                {titleText}
            </div>
            <div className="AxisLabel X">
                {xLabel}
            </div>
            <div className="AxisLabel Y">
                {yLabel}
            </div>
            <div className="Grid">
                {

                    teamSetToUse.map((team) => {
                        const xStars = getStarsFromTeam(xAxisCategory, team, playerCollectionsToUse, excludedMods)
                        const yStars = getStarsFromTeam(yAxisCategory, team, playerCollectionsToUse, excludedMods)

                        const xPos = Math.round((xStars - xMin) * pxPerStar);
                        const yPos = Math.round((yStars - yMin) * pxPerStar);
                        var title = `${standardRound(xStars)} ${xLabel},`
                        title += ` ${Math.round(yStars * 100) / 100} ${yLabel}`

                        let className = `ScatterPoint ${changingScale ? "noTrans" : ""}`

                        let tooltipVisibility: TooltipVisibility = "showOnHover";

                        if (emphasizedTeam !== undefined) {
                            const emphasize = team.id === emphasizedTeam
                            className += " " + (emphasize ? "emphasize" : "deemphasize")
                            tooltipVisibility = emphasize ? "visible" : ""

                        }


                        return (
                            <div key={team.id} className={className}
                                style={{ bottom: yPos, left: xPos }}>
                                <TeamIcon emoji={team.emoji} color={team.primaryColor} shape="Circle" />
                                <Tooltip visibility={tooltipVisibility}>{title}</Tooltip>
                            </div>
                        );
                    })
                }
                {
                    (new Array((scaleRange * 2) + 1)).fill("").map((_, index) => {
                        const dist = Math.round(index * pxPerStar / 2);
                        const xMajor = (xMin + index / 2) % 1 === 0;
                        const yMajor = (yMin + index / 2) % 1 === 0;
                        return <div key={index}>
                            <div className={`PlotLine vertical ${xMajor ? "major" : ""}`} style={{ left: dist }} />
                            <div className={`PlotLine horizontal ${yMajor ? "major" : ""}`} style={{ bottom: dist }} />
                            {
                                (xMajor && xMin + index > 0) ? (
                                    <div className={`PlotLabel vertical ${xMajor ? "major" : ""}`} style={{ left: dist }}>
                                        {xMin + index / 2}
                                    </div>
                                ) : ""
                            }
                            {
                                (yMajor && yMin + index > 0) ? (
                                    <div className={`PlotLabel horizontal ${yMajor ? "major" : ""}`} style={{ bottom: dist }}>
                                        {yMin + index / 2}
                                    </div>
                                ) : ""
                            }
                        </div>;
                    })
                }
                {
                    showAverages && <>
                        <div className="PlotLine Average vertical" style={{ left: Math.round((xAverage - xMin) * pxPerStar) }} />
                        <div className="PlotMarker Average vertical" style={{ left: Math.round((xAverage - xMin) * pxPerStar) }}>
                            <Tooltip visibility="showOnHover">Average: {standardRound(xAverage)}</Tooltip>
                        </div>
                        <div className="PlotLine Average horizontal" style={{ bottom: Math.round((yAverage - yMin) * pxPerStar) }} />
                        <div className="PlotMarker Average horizontal" style={{ bottom: Math.round((yAverage - yMin) * pxPerStar) }}>
                            <Tooltip visibility="showOnHover">Average: {standardRound(yAverage)}</Tooltip>
                        </div>
                    </>
                }
            </div>
        </div>
    )
    /*
return <div>
    <div>
        <div>
            X range from {standardRound(xRange[0])} to {standardRound(xRange[1])}
        </div>
        <div>
            Y range from {standardRound(yRange[0])} to {standardRound(yRange[1])}
        </div>
    </div>
    <div>
        {teamSet.map((teamData) => {
            const xAxisValue = teamData.categoryRatings.filter(category => category.name = xAxisCategory)[0].stars
            const yAxisValue = teamData.categoryRatings.filter(category => category.name = yAxisCategory)[0].stars

            return <div key={teamData.id}>
                {`${teamData.name}: ${xAxisValue}+${yAxisValue}`}
            </div>
        })}
    </div>
</div>
//*/
}
